"use client";

import { useEffect } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";

// Hardcoded Datadog configuration (same across all environments)
const DD_SERVICE = "mira-consumer";
const DD_SITE = "us5.datadoghq.com";
const DD_APPLICATION_ID = "38c37850-8c66-46a8-a4fb-d5ed6499be2e";
const DD_CLIENT_TOKEN = "pub8941f52d555bae7db39be8f5f7e45a31";
const DD_RUM_SAMPLE_RATE = 100;
const DD_RUM_REPLAY_SAMPLE_RATE = 0;
const DD_LOGS_SAMPLE_RATE = 100;

interface DatadogUser {
  id: string;
  email?: string | null;
  name?: string | null;
}

interface DatadogRumProviderProps {
  children: React.ReactNode;
  user?: DatadogUser | null;
}

/**
 * Initializes Datadog RUM and Browser Logs for client-side observability.
 * 
 * Usage:
 *   <DatadogRumProvider user={{ id: "123", email: "user@example.com" }}>
 *     {children}
 *   </DatadogRumProvider>
 * 
 * For pages without user context, simply omit the user prop:
 *   <DatadogRumProvider>{children}</DatadogRumProvider>
 */
export function DatadogRumProvider({ children, user }: DatadogRumProviderProps) {
  // Initialize Datadog on mount (only once)
  useEffect(() => {
    // Master flag from environment
    const ddEnabled = process.env.NEXT_PUBLIC_DD_ENABLED === "true";
    const env = process.env.NEXT_PUBLIC_APP_ENV || "";

    if (!ddEnabled) {
      return;
    }

    // Initialize RUM first (required for log correlation)
    try {
      datadogRum.init({
        applicationId: DD_APPLICATION_ID,
        clientToken: DD_CLIENT_TOKEN,
        site: DD_SITE,
        service: DD_SERVICE,
        env,
        sessionSampleRate: DD_RUM_SAMPLE_RATE,
        sessionReplaySampleRate: DD_RUM_REPLAY_SAMPLE_RATE,
        trackUserInteractions: true,
        trackResources: true,
        defaultPrivacyLevel: "mask-user-input",
      });
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Datadog RUM initialization failed:", error);
      }
    }

    // Initialize Browser Logs after RUM (ensures proper correlation)
    try {
      datadogLogs.init({
        clientToken: DD_CLIENT_TOKEN,
        site: DD_SITE,
        service: DD_SERVICE,
        env,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ["log", "debug", "info", "warn", "error"],
        sessionSampleRate: DD_LOGS_SAMPLE_RATE,
      });

      // Set global context for all browser logs
      datadogLogs.setGlobalContextProperty("log_origin", "browser");
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Datadog Browser Logs initialization failed:", error);
      }
    }
  }, []);

  // Update user context when user changes
  useEffect(() => {
    if (user) {
      datadogRum.setUser({
        id: user.id,
        email: user.email || undefined,
        name: user.name || undefined,
      });

      datadogLogs.setGlobalContextProperty("usr", {
        id: user.id,
        email: user.email,
        name: user.name,
      });
    }
  }, [user]);

  return <>{children}</>;
}

/**
 * Utility to set Datadog user from anywhere in the app.
 * Call this when user logs in or user data becomes available.
 */
export function setDatadogUser(user: DatadogUser | null) {
  if (user) {
    datadogRum.setUser({
      id: user.id,
      email: user.email || undefined,
      name: user.name || undefined,
    });
    datadogLogs.setGlobalContextProperty("usr", {
      id: user.id,
      email: user.email,
      name: user.name,
    });
  } else {
    datadogRum.clearUser();
    datadogLogs.removeGlobalContextProperty("usr");
  }
}
